import React, { useEffect, useState } from "react";
import { Plus, Search, IndianRupee, Trash2, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";

export default function AdminCustomers() {
  const [list, setList] = useState([]);
  const [agents, setAgents] = useState([]);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showCredit, setShowCredit] = useState(null);
  const [showEdit, setShowEdit] = useState(null);
  const [form, setForm] = useState({ full_name: "", phone: "", aadhaar: "", pan: "", address: "", nominee_name: "", agent_phone: "" });

  const load = () => api.get("/admin/customers").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/admin/agents").then((r) => setAgents(r.data)).catch(() => {});
  }, []);

  const add = async () => {
    if (!form.full_name || !form.phone) return toast.error("Name & phone required");
    try {
      const body = { ...form };
      if (!body.agent_phone) delete body.agent_phone;
      await api.post("/admin/customers", body);
      toast.success("Customer added");
      setShowAdd(false);
      setForm({ full_name: "", phone: "", aadhaar: "", pan: "", address: "", nominee_name: "", agent_phone: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete customer?")) return;
    try { await api.delete(`/admin/customers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = list.filter((c) => !q || c.full_name?.toLowerCase().includes(q.toLowerCase()) || c.phone?.includes(q));

  return (
    <div data-testid="admin-customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Customers</h1>
          <p className="text-sm text-slate-500">{list.length} total</p>
        </div>
        <button data-testid="open-add-customer" onClick={() => setShowAdd(true)} className="inline-flex items-center gap-2 bg-tenant-primary text-white px-4 py-2.5 rounded-xl text-sm font-bold tap-scale">
          <Plus className="w-4 h-4" /> Add Customer
        </button>
      </div>

      <div className="mt-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name/phone" className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-tenant-primary bg-white" />
      </div>

      <div className="mt-4 bg-white border border-slate-100 rounded-2xl card-shadow table-scroll">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold">
            <tr>
              <th className="text-left p-3">Name / ID</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Agent</th>
              <th className="text-right p-3">Saving</th>
              <th className="text-right p-3">Bonus</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`cust-row-${c.id}`} className="hover:bg-slate-50">
                <td className="p-3">
                  <div className="font-bold">{c.full_name}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">{c.referral_code}</div>
                </td>
                <td className="p-3 text-slate-600">+91 {c.phone}</td>
                <td className="p-3 text-slate-700">
                  {c.agent_name ? (
                    <div>
                      <div className="font-bold text-[12px]">{c.agent_name}</div>
                      <div className="text-[10px] text-slate-400">+91 {c.agent_phone}</div>
                    </div>
                  ) : (
                    <span className="text-[11px] text-slate-400 italic">Unassigned</span>
                  )}
                </td>
                <td className="p-3 text-right font-extrabold text-emerald-600">{fmtINR(c.saving_balance)}</td>
                <td className="p-3 text-right font-extrabold text-violet-700">{fmtINR(c.bonus_balance)}</td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] uppercase font-bold px-2.5 py-1 rounded-full ${c.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`edit-cust-${c.id}`} onClick={() => setShowEdit(c)} className="text-[11px] font-bold text-blue-600 mr-3 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                  <button data-testid={`credit-${c.id}`} onClick={() => setShowCredit(c)} className="text-[11px] font-bold text-tenant-primary mr-3">Credit</button>
                  <button data-testid={`del-${c.id}`} onClick={() => remove(c.id)} className="text-rose-500 inline-flex"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center p-6 text-slate-400">No customers found.</td></tr>}
          </tbody>
        </table>
      </div>

      {showAdd && <AddModal form={form} setForm={setForm} onClose={() => setShowAdd(false)} onSave={add} />}
      {showCredit && <CreditModal customer={showCredit} onClose={() => setShowCredit(null)} onDone={load} />}
      {showEdit && <EditModal customer={showEdit} agents={agents} onClose={() => setShowEdit(null)} onDone={load} />}
    </div>
  );
}

const AddModal = ({ form, setForm, onClose, onSave }) => (
  <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-3">
        <div className="font-extrabold text-lg">Add Customer</div>
        <button onClick={onClose}><X className="w-4 h-4" /></button>
      </div>
      <div className="space-y-3">
        {[
          ["full_name", "Full Name *"], ["phone", "Mobile *"], ["aadhaar", "Aadhaar"],
          ["pan", "PAN"], ["address", "Address"], ["nominee_name", "Nominee"],
        ].map(([k, l]) => (
          <div key={k}>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{l}</label>
            <input data-testid={`add-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
          </div>
        ))}
        <div className="bg-blue-50/60 border border-blue-200 rounded-2xl p-3">
          <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-blue-700">Link to Agent (optional)</label>
          <input
            data-testid="add-agent_phone"
            placeholder="Agent's mobile number (10 digits)"
            value={form.agent_phone}
            onChange={(e) => setForm({ ...form, agent_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
            className="mt-1.5 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary bg-white"
          />
          <p className="text-[10px] text-slate-500 mt-1">Customer will be auto-assigned to this agent.</p>
        </div>
        <button data-testid="confirm-add-customer" onClick={onSave} className="w-full bg-tenant-primary text-white py-3 rounded-xl font-bold">Save</button>
      </div>
    </div>
  </div>
);

const CreditModal = ({ customer, onClose, onDone }) => {
  const [wallet, setWallet] = useState("saving");
  const [type, setType] = useState("credit");
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");

  const submit = async () => {
    try {
      await api.post("/admin/wallet/manual", {
        customer_id: customer.id, wallet, type, amount: parseFloat(amount),
        remarks,
      });
      toast.success("Wallet updated");
      onClose(); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <div className="font-extrabold text-lg">Manual Wallet Adjust</div>
        <div className="text-sm text-slate-500">{customer.full_name}</div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={() => setWallet("saving")} className={`py-2 rounded-xl text-xs font-bold ${wallet === "saving" ? "bg-emerald-600 text-white" : "bg-slate-100"}`}>Saving</button>
          <button onClick={() => setWallet("bonus")} className={`py-2 rounded-xl text-xs font-bold ${wallet === "bonus" ? "bg-violet-600 text-white" : "bg-slate-100"}`}>Bonus</button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={() => setType("credit")} className={`py-2 rounded-xl text-xs font-bold ${type === "credit" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-300" : "bg-slate-100"}`}>Credit</button>
          <button onClick={() => setType("debit")} className={`py-2 rounded-xl text-xs font-bold ${type === "debit" ? "bg-rose-50 text-rose-700 ring-1 ring-rose-300" : "bg-slate-100"}`}>Debit</button>
        </div>
        <div className="mt-3 flex items-center gap-2 border-2 border-slate-200 rounded-xl px-3 py-2.5">
          <IndianRupee className="w-4 h-4 text-slate-400" />
          <input data-testid="credit-amount" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="Amount" className="flex-1 outline-none font-bold" />
        </div>
        <input data-testid="credit-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks" className="mt-2 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none" />
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button onClick={onClose} className="py-2.5 rounded-xl border border-slate-200 font-bold">Cancel</button>
          <button data-testid="confirm-credit" onClick={submit} className="py-2.5 rounded-xl bg-tenant-primary text-white font-bold">Apply</button>
        </div>
      </div>
    </div>
  );
};

const EditModal = ({ customer, agents, onClose, onDone }) => {
  const [form, setForm] = useState({
    full_name: customer.full_name || "",
    aadhaar: customer.aadhaar || "",
    pan: customer.pan || "",
    address: customer.address || "",
    nominee_name: customer.nominee_name || "",
    agent_id: customer.agent_id || "",
    status: customer.status || "active",
  });
  const [policy, setPolicy] = useState(null);
  const [dailyAmount, setDailyAmount] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/policies").then((r) => {
      const p = r.data.find((x) => x.customer_id === customer.id && x.status === "active");
      if (p) {
        setPolicy(p);
        setDailyAmount(String(p.daily_amount || ""));
        setEndDate(p.end_date ? p.end_date.slice(0, 10) : "");
      }
    }).catch(() => {});
  }, [customer.id]);

  const save = async () => {
    if (!form.full_name) return toast.error("Name required");
    setSaving(true);
    try {
      const body = {
        full_name: form.full_name,
        aadhaar: form.aadhaar || null,
        pan: form.pan || null,
        address: form.address || null,
        nominee_name: form.nominee_name || null,
        agent_id: form.agent_id || null,
        status: form.status,
      };
      await api.patch(`/admin/customers/${customer.id}`, body);

      if (policy) {
        const polUpd = {};
        const newDaily = parseFloat(dailyAmount);
        if (!isNaN(newDaily) && newDaily !== policy.daily_amount) polUpd.daily_amount = newDaily;
        if (endDate && endDate !== (policy.end_date || "").slice(0, 10)) polUpd.end_date = endDate;
        if (Object.keys(polUpd).length) {
          await api.patch(`/admin/policies/${policy.id}`, polUpd);
        }
      }

      toast.success("Customer updated");
      onClose();
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={onClose}>
      <div data-testid="edit-customer-modal" className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-3">
          <div>
            <div className="font-extrabold text-lg">Edit Customer</div>
            <div className="text-[11px] text-slate-500">+91 {customer.phone} · {customer.referral_code}</div>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3">
          {[
            ["full_name", "Full Name *", "text"],
            ["aadhaar", "Aadhaar", "text"],
            ["pan", "PAN", "text"],
            ["address", "Address", "text"],
            ["nominee_name", "Nominee", "text"],
          ].map(([k, l, t]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">{l}</label>
              <input data-testid={`edit-cust-${k}`} type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary" />
            </div>
          ))}

          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Assigned Agent</label>
            <select data-testid="edit-cust-agent_id" value={form.agent_id || ""} onChange={(e) => setForm({ ...form, agent_id: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary bg-white">
              <option value="">— Unassigned —</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.full_name} ({a.agent_code})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Status</label>
            <select data-testid="edit-cust-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary bg-white">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          {policy ? (
            <div className="bg-amber-50/60 border border-amber-200 rounded-2xl p-3 mt-2">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-700 mb-2">Active Policy · {policy.policy_number}</div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Daily Collection Amount (₹)</label>
                <div className="mt-1 flex items-center gap-2 border-2 border-slate-200 rounded-xl px-3 py-2 bg-white">
                  <IndianRupee className="w-4 h-4 text-slate-400" />
                  <input data-testid="edit-cust-daily_amount" type="number" value={dailyAmount} onChange={(e) => setDailyAmount(e.target.value)} className="flex-1 outline-none font-bold" />
                </div>
              </div>
              <div className="mt-2">
                <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Policy End Date</label>
                <input data-testid="edit-cust-end_date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-tenant-primary bg-white" />
              </div>
            </div>
          ) : (
            <div className="text-[11px] text-slate-400 italic">No active policy attached — create one in Policies tab.</div>
          )}

          <button data-testid="confirm-edit-customer" onClick={save} disabled={saving} className="w-full bg-tenant-primary text-white py-3 rounded-xl font-bold disabled:opacity-60">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};
